import type { Identifier } from "ra-core";
import { useSyncExternalStore } from "react";

// Når nettet faller ut midt i en stopp, vet vi ikke om serveren fikk den.
// Da står timeren som «Uavklart» til serveren har bekreftet, aldri som
// «Lagret» (PRD US06). Stoppen huskes også i localStorage, så den sendes
// på nytt etter en omlasting. stop_time_entry er idempotent: første
// stopptid står, så det er trygt å sende den flere ganger.

export class UnsettledError extends Error {
  unsettled = true;
}

// Fikk vi et svar fra serveren? Uten svar er utfallet ukjent.
export const isNetworkFailure = (error: unknown) => {
  const e = error as { status?: number; message?: string; name?: string };
  if ((error as UnsettledError)?.unsettled) return true;
  if (e?.status && e.status > 0) return false;
  return /Failed to fetch|NetworkError|Load failed|network|fetch/i.test(
    `${e?.name ?? ""} ${e?.message ?? ""}`,
  );
};

export type PendingStop = { entryId: Identifier; endedAt: string };

const STORAGE_KEY = "mimin.pendingTimerStop";
const listeners = new Set<() => void>();

const read = (): PendingStop | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingStop) : null;
  } catch {
    return null;
  }
};

let pending: PendingStop | null = typeof window === "undefined" ? null : read();

export const getPendingStop = () => pending;

export const setPendingStop = (next: PendingStop | null) => {
  pending = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Lagring kan være blokkert; minnet holder for denne økten.
  }
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const usePendingStop = () =>
  useSyncExternalStore(subscribe, getPendingStop, () => null);
